import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ReporteJustificativaDialogComponent } from '../reporte-justificativa-dialog/reporte-justificativa-dialog.component'
import { ReporteHistoricoDialogComponent } from '../reporte-historico-dialog/reporte-historico-dialog.component'
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/modules/confirm-dialog/confirm-dialog.component'

export interface ArquivoData {
  tituloDoArquivo: string
  urlDoArquivo: string
  nota?: number | null
  envelopeEnviado?: boolean
  peso?: number
  onNotaSave?: (nota: number) => void
  inscricaoDocumentoId?: number
  jaReportado?: boolean
  onReporteIdentificacao?: (justificativa: string) => void
  temReportePendente?: boolean
  reporteData?: any[]
  onDecisaoReporte?: (acao: string, justificativa: string) => void
}

@Component({
  selector: 'app-arquivo-viewer-avaliacao',
  templateUrl: 'arquivo-viewer-avaliacao.component.html',
  styleUrls: ['./arquivo-viewer-avaliacao.component.css'],
})
export class ArquivoViewerAvaliacaoComponent implements AfterViewInit, OnDestroy {
  notaAtual: number | null = null
  peso: number = 0
  isFullscreen = false
  mostrarConfirmacaoDecisao: 'liberar' | 'desclassificar' | null = null
  justificativaDecisao = ''
  notaConfirmada = false
  private notaDirty = false

  private arquivoIframe?: HTMLIFrameElement

  constructor(
    private el: ElementRef,
    public dialogRef: MatDialogRef<ArquivoViewerAvaliacaoComponent>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ArquivoData
  ) {
    this.notaAtual = data.nota ?? null
    this.peso = data.peso ?? 0

    if (data.onNotaSave) {
      this.dialogRef.disableClose = true
      this.dialogRef.backdropClick().subscribe(() => this.fechar())
      this.dialogRef.keydownEvents().subscribe(event => {
        if (event.key === 'Escape') {
          event.preventDefault()
          this.fechar()
        }
      })
    }
  }

  ngAfterViewInit(): void {
    this.checkScreenResolution()
    this.loadFileWithAuth()
  }

  ngOnDestroy(): void {
    const fullscreenEl = document.fullscreenElement || (document as any).webkitFullscreenElement
    if (fullscreenEl) {
      const exit = document.exitFullscreen || (document as any).webkitExitFullscreen
      if (exit) exit.call(document).catch(() => {})
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedNota()) {
      event.preventDefault()
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!(document.fullscreenElement || (document as any).webkitFullscreenElement)
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      const el = this.el.nativeElement
      const request = el.requestFullscreen || el.webkitRequestFullscreen
      if (request) request.call(el).catch(() => {})
    }
  }

  private checkScreenResolution(): void {
    if (window.innerWidth < 1366 || window.innerHeight < 768) {
      this.snackBar.open(
        'Para melhor experiência de correção, recomendamos uma resolução mínima de 1366×768. Use o botão de tela cheia para maximizar a visualização.',
        'Entendi',
        { duration: 10000, verticalPosition: 'top' }
      )
    }
  }

  private loadFileWithAuth(): void {
    const token = this.getToken()
    if (!token) {
      console.error('Token não encontrado. Usuário pode não estar autenticado.')
      return
    }

    fetch(this.data.urlDoArquivo, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Erro ao carregar arquivo: ${response.statusText} (${response.status})`)
        }
        return response.blob()
      })
      .then(blob => {
        const mimeType = blob.type
        const url = URL.createObjectURL(blob)

        this.arquivoIframe = this.el.nativeElement.querySelector('iframe')
        if (!this.arquivoIframe) return

        if (mimeType === 'application/pdf') {
          this.arquivoIframe.src = url
        } else if (mimeType.startsWith('image/')) {
          this.arquivoIframe.srcdoc = `<img src="${url}" style="width: 100%; height: 100%; object-fit: contain;" />`
        } else if (mimeType === 'video/mp4') {
          this.arquivoIframe.srcdoc = `
            <video controls style="width: 100%; height: 100%">
              <source src="${url}" type="video/mp4">
              Seu navegador não suporta a reprodução de vídeos.
            </video>
          `
        } else {
          const link = document.createElement('a')
          link.href = url
          link.download = this.data.tituloDoArquivo || 'arquivo'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.dialogRef.close()
        }
      })
      .catch(err => {
        console.error('Erro ao exibir o arquivo:', err)
      })
  }

  private getToken(): string {
    return localStorage.getItem('access_token') || ''
  }

  onNotaConfirmation(nota: number): void {
    if (this.data.onNotaSave) {
      this.data.onNotaSave(nota)
      this.notaConfirmada = true
    }
  }

  fechar(): void {
    if (this.hasUnsavedNota()) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: new ConfirmDialogModel(
          'Nota não salva',
          'Você digitou uma nota mas não confirmou. Deseja sair sem salvar?',
          'Sair sem salvar',
          'Continuar avaliando'
        ),
      })
      dialogRef.afterClosed().subscribe((confirmou: boolean) => {
        if (confirmou) {
          this.dialogRef.close()
        }
      })
    } else {
      this.dialogRef.close()
    }
  }

  private hasUnsavedNota(): boolean {
    if (!this.data.onNotaSave || this.isEnvelopeEnviado()) return false
    return this.notaDirty && !this.notaConfirmada
  }

  onNotaChanged(): void {
    this.notaDirty = true
    this.notaConfirmada = false
  }

  isEnvelopeEnviado(): boolean {
    return this.data.envelopeEnviado ?? false
  }

  reportarIdentificacao(): void {
    if (this.data.onReporteIdentificacao) {
      const dialogRef = this.dialog.open(ReporteJustificativaDialogComponent, {
        width: '90vw',
        maxWidth: '600px',
        autoFocus: true,
      })

      dialogRef.afterClosed().subscribe((justificativa: string | null) => {
        if (justificativa) {
          this.data.onReporteIdentificacao!(justificativa)
          this.dialogRef.close()
        }
      })
    }
  }

  canReportar(): boolean {
    return !!this.data.inscricaoDocumentoId && !this.isEnvelopeEnviado() && !this.jaReportado()
  }

  jaReportado(): boolean {
    return !!this.data.jaReportado
  }

  iniciarDecisao(acao: 'liberar' | 'desclassificar'): void {
    this.mostrarConfirmacaoDecisao = acao
    this.justificativaDecisao = ''
  }

  cancelarDecisao(): void {
    this.mostrarConfirmacaoDecisao = null
  }

  confirmarDecisao(): void {
    if (!this.justificativaDecisao.trim() || !this.mostrarConfirmacaoDecisao) return
    if (this.data.onDecisaoReporte) {
      this.data.onDecisaoReporte(this.mostrarConfirmacaoDecisao, this.justificativaDecisao.trim())
    }
    this.dialogRef.close()
  }

  abrirHistoricoReporte(): void {
    if (this.data.reporteData && this.data.reporteData.length > 0) {
      this.dialog.open(ReporteHistoricoDialogComponent, {
        data: { reportes: this.data.reporteData, mostrarIndice: true },
        width: '60vw',
        maxWidth: '90vw',
        autoFocus: false,
      })
    }
  }
}
